import React, { useState, useEffect, useRef } from 'react';
import API from '../utils/api';

export default function AdminGallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ caption: '', category: 'general', isCollage: false });
  const fileRef = useRef();

  const load = () => {
    setLoading(true);
    API.get('/gallery/all')
      .then(r => { if (r.data.success) setItems(r.data.data); })
      .catch(() => { })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('image', file);
    fd.append('caption', form.caption);
    fd.append('category', form.category);
    fd.append('isCollage', form.isCollage);
    try {
      const r = await API.post('/gallery', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (r.data.success) {
        setItems(prev => [r.data.data, ...prev]);
        setForm({ caption: '', category: 'general', isCollage: false });
        fileRef.current.value = '';
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const toggle = async (id, active) => {
    const r = await API.patch(`/gallery/${id}`, { active });
    if (r.data.success) setItems(prev => prev.map(i => i._id === id ? r.data.data : i));
  };

  const del = async (id) => {
    if (!window.confirm('Delete this image?')) return;
    await API.delete(`/gallery/${id}`);
    setItems(prev => prev.filter(i => i._id !== id));
  };

  const collageItems = items.filter(i => i.isCollage);
  const extraItems = items.filter(i => !i.isCollage);

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <div>
          <h2 className="admin-page__title">Gallery Management</h2>
          <p className="admin-page__sub">{collageItems.length} collage · {extraItems.length} extra</p>
        </div>
      </div>

      {/* Upload Form */}
      <div className="admin-card">
        <h3 className="admin-card__title">Upload Image</h3>
        <form onSubmit={handleUpload} className="admin-form">
          <div className="admin-form__row">
            <div className="admin-fg">
              <label className="admin-fl">Image File *</label>
              <input type="file" ref={fileRef} accept="image/*" className="admin-fi" required />
            </div>
            <div className="admin-fg">
              <label className="admin-fl">Caption</label>
              <input type="text" className="admin-fi" value={form.caption}
                onChange={e => setForm(f => ({ ...f, caption: e.target.value }))}
                placeholder="e.g. Bridal Transformation" />
            </div>
          </div>
          <div className="admin-form__row">
            <div className="admin-fg">
              <label className="admin-fl">Category</label>
              <select className="admin-select" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                <option value="general">General</option>
                <option value="bridal">Bridal</option>
                <option value="hair">Hair</option>
                <option value="makeup">Makeup</option>
                <option value="skincare">Skincare</option>
                <option value="nails">Nails</option>
                <option value="spa">Spa</option>
              </select>
            </div>
            <div className="admin-fg admin-fg--checkbox">
              <label className="admin-checkbox-label">
                <input type="checkbox" checked={form.isCollage}
                  onChange={e => setForm(f => ({ ...f, isCollage: e.target.checked }))} />
                Add to Hero Collage (main 8-grid)
              </label>
            </div>
          </div>
          <button type="submit" className="admin-btn admin-btn--primary" disabled={uploading}>
            {uploading ? 'Uploading…' : '↑ Upload Image'}
          </button>
        </form>
      </div>

      {loading ? <div className="admin-loading">Loading gallery…</div> : (
        <>
          <h3 className="admin-section-label">Hero Collage ({collageItems.length}/8)</h3>
          <div className="admin-gallery-grid">
            {collageItems.map(item => (
              <GalleryThumb key={item._id} item={item} onToggle={toggle} onDelete={del} />
            ))}
            {collageItems.length === 0 && <p className="admin-empty admin-empty--sm">No collage images. Upload one above and check "Hero Collage".</p>}
          </div>

          <h3 className="admin-section-label" style={{ marginTop: '32px' }}>Extra / See More Images ({extraItems.length})</h3>
          <div className="admin-gallery-grid">
            {extraItems.map(item => (
              <GalleryThumb key={item._id} item={item} onToggle={toggle} onDelete={del} />
            ))}
            {extraItems.length === 0 && <p className="admin-empty admin-empty--sm">No extra images yet.</p>}
          </div>
        </>
      )}
    </div>
  );
}

function GalleryThumb({ item, onToggle, onDelete }) {
  return (
    <div className={`admin-gallery-thumb${!item.active ? ' inactive' : ''}`}>
      <img src={item.imageUrl} alt={item.caption} className="admin-gallery-thumb__img" />
      <div className="admin-gallery-thumb__info">
        <span className="admin-gallery-thumb__caption">{item.caption || '—'}</span>
        <span className="admin-gallery-thumb__cat">{item.category}</span>
      </div>
      <div className="admin-gallery-thumb__actions">
        <button className="admin-btn admin-btn--outline admin-btn--xs"
          onClick={() => onToggle(item._id, !item.active)}>
          {item.active ? 'Hide' : 'Show'}
        </button>
        <button className="admin-btn admin-btn--danger admin-btn--xs" onClick={() => onDelete(item._id)}>✕</button>
      </div>
    </div>
  );
}
